(() => {
  if (window.__joffzGalleryV46) return;
  window.__joffzGalleryV46 = true;

  const states = new Map();
  const cache = new Map();
  let activeState = null;

  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const galleryItems = (root) => Array.from(root.querySelectorAll('[data-jpc-gallery-open]'));
  const plainPrimaryClick = (event) =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  const getItemData = (item) => {
    const thumbnail = item?.querySelector('img');
    const desktopSource = item?.dataset.jpcGallerySrc || item?.href || '';
    const mobileSource = item?.dataset.jpcGallerySrcMobile || '';
    const useMobileSource = window.matchMedia?.('(max-width: 749px)').matches;

    return {
      source: useMobileSource && mobileSource ? mobileSource : desktopSource,
      fallback: thumbnail?.currentSrc || thumbnail?.src || '',
      alt: item?.dataset.jpcGalleryAlt || thumbnail?.alt || '',
    };
  };

  const preload = (url, priority = 'auto') => {
    if (!url) return Promise.reject(new Error('Missing gallery image URL'));

    const existing = cache.get(url);
    if (existing) return existing.promise;

    const record = { status: 'loading', promise: null };
    record.promise = new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      if ('fetchPriority' in image) image.fetchPriority = priority;

      let settled = false;

      const finish = async () => {
        if (settled) return;
        settled = true;

        try {
          if (typeof image.decode === 'function') await image.decode();
        } catch (error) {
          /* A decoded bitmap is still usable when decode() races with load completion. */
        }

        record.status = 'ready';
        resolve(url);
      };

      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', () => {
        if (settled) return;
        settled = true;
        cache.delete(url);
        reject(new Error(`Unable to preload ${url}`));
      }, { once: true });

      image.src = url;
      if (image.complete && image.naturalWidth > 0) finish();
    });

    cache.set(url, record);
    return record.promise;
  };

  const setBusy = (state) => {
    clearTimeout(state.busyTimer);
    state.busyTimer = window.setTimeout(() => {
      if (state.opened && state.pendingRequest) state.dialog.setAttribute('aria-busy', 'true');
    }, 140);
  };

  const clearBusy = (state) => {
    clearTimeout(state.busyTimer);
    state.busyTimer = 0;
    state.dialog.removeAttribute('aria-busy');
  };

  const swapImage = (state, url, alt, instant = false) => {
    if (!url) return;

    const current = state.activeImage;
    const currentUrl = current?.currentSrc || current?.src || '';
    if (current && currentUrl === url) {
      current.alt = alt || '';
      return;
    }

    const next = state.images.find((candidate) => candidate !== current) || state.images[0];
    next.alt = alt || '';
    next.src = url;

    if (instant || reducedMotion() || !current) {
      state.images.forEach((image) => image.classList.remove('is-active'));
      next.classList.add('is-active');
    } else {
      next.classList.remove('is-active');
      requestAnimationFrame(() => {
        next.classList.add('is-active');
        current.classList.remove('is-active');
      });
    }

    state.activeImage = next;
  };

  const warmAdjacent = (state, items, index) => {
    if (!state.opened || items.length < 2) return;

    const indices = [
      (index + 1) % items.length,
      (index - 1 + items.length) % items.length,
    ];

    indices.forEach((itemIndex) => {
      const source = getItemData(items[itemIndex]).source;
      if (source) preload(source, 'low').catch(() => {});
    });
  };

  const showSlide = async (state, requestedIndex, options = {}) => {
    const items = galleryItems(state.root);
    if (!items.length) return false;

    const index = ((requestedIndex % items.length) + items.length) % items.length;
    const data = getItemData(items[index]);
    if (!data.source && !data.fallback) return false;

    state.index = index;
    const requestId = ++state.requestId;
    state.pendingRequest = true;

    if (state.counter) state.counter.textContent = `${index + 1} / ${items.length}`;
    if (state.previous) state.previous.disabled = items.length < 2;
    if (state.next) state.next.disabled = items.length < 2;

    const cached = data.source ? cache.get(data.source) : null;
    if (cached?.status === 'ready') {
      swapImage(state, data.source, data.alt, Boolean(options.instant));
      state.pendingRequest = false;
      clearBusy(state);
      warmAdjacent(state, items, index);
      return true;
    }

    if (data.fallback) swapImage(state, data.fallback, data.alt, Boolean(options.instant));
    setBusy(state);

    try {
      const finalUrl = data.source || data.fallback;
      await preload(finalUrl, 'high');
      if (!state.opened || requestId !== state.requestId) return true;
      swapImage(state, finalUrl, data.alt, false);
    } catch (error) {
      if (state.opened && requestId === state.requestId && data.fallback) {
        swapImage(state, data.fallback, data.alt, false);
      }
    } finally {
      if (requestId === state.requestId) {
        state.pendingRequest = false;
        clearBusy(state);
      }
    }

    warmAdjacent(state, items, index);
    return true;
  };

  const finishClose = (state) => {
    if (!state?.opened) return;

    state.opened = false;
    state.pendingRequest = false;
    state.requestId += 1;
    state.pointerStart = null;
    clearBusy(state);
    state.observer?.disconnect();
    state.observer = null;

    if (activeState === state) activeState = null;
    if (!activeState) document.documentElement.classList.remove('jpc-gallery-modal-open');

    if (state.restoreFocus && state.trigger?.isConnected) {
      state.trigger.focus({ preventScroll: true });
    }

    state.images.forEach((image, index) => {
      image.removeAttribute('src');
      image.alt = '';
      image.classList.toggle('is-active', index === 0);
    });
    state.activeImage = state.images[0];
  };

  const close = (state, restoreFocus = true) => {
    if (!state) return;
    state.restoreFocus = restoreFocus;

    try {
      if (state.dialog.open) state.dialog.close();
    } catch (error) {
      state.dialog.remove();
      states.delete(state.root);
    }

    finishClose(state);
  };

  const prepare = (root) => {
    const saved = states.get(root);
    if (saved?.dialog.isConnected) return saved;

    const dialog = root.querySelector('dialog[data-jpc-media-dialog]');
    const originalImage = dialog?.querySelector('[data-jpc-dialog-image]');
    const viewport = dialog?.querySelector('.jpc-media-dialog__viewport');
    const closeButton = dialog?.querySelector('.jpc-media-dialog__close');

    if (!dialog || !originalImage || !viewport || !closeButton || typeof dialog.showModal !== 'function') {
      return null;
    }

    originalImage.decoding = 'async';
    originalImage.removeAttribute('src');
    originalImage.classList.add('is-active');

    const bufferImage = originalImage.cloneNode(false);
    bufferImage.removeAttribute('src');
    bufferImage.removeAttribute('data-jpc-dialog-image');
    bufferImage.dataset.jpcDialogBuffer = '';
    bufferImage.alt = '';
    bufferImage.classList.remove('is-active');
    viewport.appendChild(bufferImage);

    const state = {
      root,
      dialog,
      viewport,
      closeButton,
      images: [originalImage, bufferImage],
      activeImage: originalImage,
      previous: dialog.querySelector('[data-jpc-dialog-prev]'),
      next: dialog.querySelector('[data-jpc-dialog-next]'),
      counter: dialog.querySelector('[data-jpc-dialog-counter]'),
      opened: false,
      restoreFocus: true,
      trigger: null,
      observer: null,
      requestId: 0,
      pendingRequest: false,
      busyTimer: 0,
      pointerStart: null,
      index: 0,
    };

    dialog.dataset.jpcOwnerId = root.id;
    document.body.appendChild(dialog);

    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      close(state);
    });

    state.previous?.addEventListener('click', () => showSlide(state, state.index - 1));
    state.next?.addEventListener('click', () => showSlide(state, state.index + 1));

    dialog.addEventListener('close', () => {
      if (!dialog.open) finishClose(state);
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close(state);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target === viewport) close(state);
    });

    viewport.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary) return;
      state.pointerStart = {
        x: event.clientX,
        y: event.clientY,
        id: event.pointerId,
      };
    }, { passive: true });

    viewport.addEventListener('pointerup', (event) => {
      const start = state.pointerStart;
      state.pointerStart = null;
      if (!start || start.id !== event.pointerId || !state.opened) return;

      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.abs(dx) < 46 || Math.abs(dx) < Math.abs(dy) * 1.15) return;

      showSlide(state, state.index + (dx < 0 ? 1 : -1));
    }, { passive: true });

    viewport.addEventListener('pointercancel', () => {
      state.pointerStart = null;
    }, { passive: true });

    dialog.addEventListener('keydown', (event) => {
      if (!state.opened) return;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        showSlide(state, state.index + (event.key === 'ArrowRight' ? 1 : -1));
      }

      if (event.key === 'Tab') {
        const focusable = Array.from(dialog.querySelectorAll('button:not([disabled]), a[href]'))
          .filter((node) => node.getClientRects().length);

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    });

    states.set(root, state);
    return state;
  };

  const open = (opener) => {
    const root = opener.closest('joffz-product-configurator');
    if (!root) return false;

    const state = prepare(root);
    if (!state) return false;

    if (getComputedStyle(state.dialog).getPropertyValue('--jpc-lightbox-ready').trim() !== '1') {
      return false;
    }

    if (activeState) close(activeState, false);

    const items = galleryItems(root);
    const index = Math.max(0, items.indexOf(opener));
    const data = getItemData(opener);

    state.trigger = opener;
    state.restoreFocus = true;
    state.index = index;
    state.opened = true;
    state.pendingRequest = false;

    swapImage(state, data.fallback || data.source, data.alt, true);

    if (state.counter) state.counter.textContent = `${index + 1} / ${items.length}`;
    if (state.previous) state.previous.disabled = items.length < 2;
    if (state.next) state.next.disabled = items.length < 2;

    try {
      state.dialog.showModal();

      const styles = getComputedStyle(state.dialog);
      const rect = state.dialog.getBoundingClientRect();
      if (
        styles.display === 'none' ||
        styles.visibility !== 'visible' ||
        Number(styles.opacity) === 0 ||
        rect.width < 40 ||
        rect.height < 40
      ) {
        close(state, false);
        return false;
      }

      activeState = state;
      document.documentElement.classList.add('jpc-gallery-modal-open');
      state.closeButton.focus({ preventScroll: true });

      showSlide(state, index, { instant: true });

      state.observer = new MutationObserver(() => {
        if (!root.isConnected || !state.dialog.isConnected) {
          close(state, false);
          state.dialog.remove();
          states.delete(root);
        }
      });
      state.observer.observe(document.body, { childList: true, subtree: true });

      return true;
    } catch (error) {
      state.opened = Boolean(state.dialog.open);
      close(state, false);
      return false;
    }
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || !plainPrimaryClick(event)) return;
    const opener = event.target.closest?.('[data-jpc-gallery-open]');
    if (opener && open(opener)) event.preventDefault();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    states.forEach((state, root) => {
      if (!root.isConnected || event.target.contains(root) || root.dataset.sectionId === event.detail?.sectionId) {
        close(state, false);
        state.dialog.remove();
        states.delete(root);
      }
    });
  });

  window.addEventListener('pagehide', () => close(activeState, false));
  window.addEventListener('pageshow', () => close(activeState, false));
})();
